import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  User, 
  Mail, 
  Phone, 
  Building, 
  Globe, 
  Linkedin, 
  Instagram,
  Youtube,
  Facebook,
  Twitter,
  MessageCircle,
  Github,
  Loader2,
  CheckCircle,
  AlertCircle,
  Clock,
  Save,
  Camera,
  ImageIcon
} from 'lucide-react';
import ImageCropper from '../components/ImageCropper';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PhotoUpload({ currentPhoto, token, onUploaded }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [cropImage, setCropImage] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('errors.invalidImageType'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('errors.imageTooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCropImage(reader.result);
    reader.readAsDataURL(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleCropDone = async (blob) => {
    setCropImage(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', blob, 'photo.jpg');
      const res = await fetch(`${API}/profile/edit/${token}/photo`, { method: 'POST', body: fd });
      if (res.ok) {
        const data = await res.json();
        onUploaded(data.photo);
        toast.success(t('owner.photoUploaded'));
      } else {
        const err = await res.json();
        toast.error(err.detail || t('errors.uploadFailed'));
      }
    } catch {
      toast.error(t('errors.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const photoSrc = currentPhoto
    ? currentPhoto.startsWith('http') ? currentPhoto : `${process.env.REACT_APP_BACKEND_URL}${currentPhoto}`
    : null;

  return (
    <div className="flex items-center gap-5" data-testid="photo-upload-section">
      {cropImage && (
        <ImageCropper
          image={cropImage}
          cropShape="round"
          aspect={1}
          onCropDone={handleCropDone}
          onCancel={() => setCropImage(null)}
        />
      )}
      <div className="relative w-20 h-20 rounded-full bg-slate-100 border-2 border-slate-200 overflow-hidden flex-shrink-0">
        {photoSrc ? (
          <img src={photoSrc} alt="Profile" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User className="w-8 h-8 text-slate-400" />
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-white animate-spin" />
          </div>
        )}
      </div>
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="rounded-full"
          data-testid="upload-photo-btn"
        >
          <Camera className="w-4 h-4 mr-2" />
          {t('owner.uploadPhoto')}
        </Button>
        <p className="text-xs text-slate-500 mt-1">{t('owner.photoRequirements')}</p>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
      </div>
    </div>
  );
}

function BannerUpload({ currentBanner, token, onUploaded }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [cropImage, setCropImage] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('errors.invalidImageType'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('errors.imageTooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCropImage(reader.result);
    reader.readAsDataURL(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleCropDone = async (blob) => {
    setCropImage(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', blob, 'banner.jpg');
      const res = await fetch(`${API}/profile/edit/${token}/banner`, { method: 'POST', body: fd });
      if (res.ok) {
        const data = await res.json();
        onUploaded(data.banner);
        toast.success(t('owner.bannerUpdated'));
      } else {
        const err = await res.json();
        toast.error(err.detail || t('errors.uploadFailed'));
      }
    } catch {
      toast.error(t('errors.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const bannerSrc = currentBanner?.type === 'image' && currentBanner?.value
    ? currentBanner.value.startsWith('http') ? currentBanner.value : `${process.env.REACT_APP_BACKEND_URL}${currentBanner.value}`
    : null;
  const bannerColor = currentBanner?.type === 'color' ? currentBanner.value : null;

  return (
    <div data-testid="banner-upload-section">
      {cropImage && (
        <ImageCropper
          image={cropImage}
          cropShape="rect"
          aspect={16 / 9}
          onCropDone={handleCropDone}
          onCancel={() => setCropImage(null)}
        />
      )}
      <div
        className="relative w-full h-28 rounded-xl overflow-hidden border-2 border-dashed border-slate-200 mb-3"
        style={bannerColor ? { backgroundColor: bannerColor } : undefined}
      >
        {bannerSrc ? (
          <img src={bannerSrc} alt="Banner" className="w-full h-full object-cover" />
        ) : !bannerColor ? (
          <div className="w-full h-full flex items-center justify-center bg-slate-50">
            <ImageIcon className="w-8 h-8 text-slate-300" />
          </div>
        ) : null}
        {uploading && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-white animate-spin" />
          </div>
        )}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="rounded-full"
        data-testid="upload-banner-btn"
      >
        <ImageIcon className="w-4 h-4 mr-2" />
        {t('owner.uploadBannerImage')}
      </Button>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
    </div>
  );
}

export default function PublicProfileEdit() {
  const { token } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [banner, setBanner] = useState(null);
  
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', job_title: '', company: '', bio: '',
    phone: '', office_phone: '', email: '', website: '',
    linkedin: '', instagram: '', whatsapp: '', youtube_channel: '',
    youtube_video: '', facebook: '', twitter: '', tiktok: '', github: '', discord: ''
  });

  useEffect(() => {
    fetchProfileData();
  }, [token]);

  const fetchProfileData = async () => {
    try {
      const response = await fetch(`${API}/profile/edit/${token}`);
      if (response.ok) {
        const data = await response.json();
        setProfile(data.profile);
        setExpiresAt(data.expires_at);
        setPhotoUrl(data.profile.photo || null);
        setBanner(data.profile.banner || null);
        setFormData({
          first_name: data.profile.first_name || '',
          last_name: data.profile.last_name || '',
          job_title: data.profile.job_title || '',
          company: data.profile.company || '',
          bio: data.profile.bio || '',
          phone: data.profile.phone || '',
          office_phone: data.profile.office_phone || '',
          email: data.profile.email || '',
          website: data.profile.website || '',
          linkedin: data.profile.linkedin || '',
          instagram: data.profile.instagram || '',
          whatsapp: data.profile.whatsapp || '',
          youtube_channel: data.profile.youtube_channel || '',
          youtube_video: data.profile.youtube_video || '',
          facebook: data.profile.facebook || '',
          twitter: data.profile.twitter || '',
          tiktok: data.profile.tiktok || '',
          github: data.profile.github || '',
          discord: data.profile.discord || ''
        });
      } else {
        const errData = await response.json();
        setError(errData.detail || t('profileEdit.invalidLink'));
      }
    } catch {
      setError(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch(`${API}/profile/edit/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setSaved(true);
        toast.success(t('profileEdit.saved'));
      } else {
        const errData = await response.json();
        toast.error(errData.detail || t('errors.serverError'));
      }
    } catch {
      toast.error(t('errors.serverError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <Loader2 className="w-8 h-8 text-[#0A2540] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-4">{t('profileEdit.linkExpired')}</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="btn-primary rounded-full">{t('common.backToHome')}</Button>
        </motion.div>
      </div>
    );
  }

  if (saved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] px-4">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-4">{t('profileEdit.success')}</h1>
          <p className="text-slate-600 mb-6">{t('profileEdit.successMessage')}</p>
          <a href={`/user/${profile.profile_id}`} className="inline-flex items-center justify-center px-6 py-3 bg-[#00C48C] text-white rounded-full hover:bg-[#00A67E] transition-colors">
            {t('profileEdit.viewProfile')}
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <div className="w-16 h-16 bg-[#0A2540]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-[#0A2540]" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">{t('profileEdit.title')}</h1>
          <p className="text-slate-600">{t('profileEdit.subtitle')}</p>
          {expiresAt && (
            <div className="mt-4 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-2">
              <Clock className="w-4 h-4 text-amber-600" />
              <span className="text-sm text-amber-700">
                {t('profileEdit.expiresOn')}: {new Date(expiresAt).toLocaleDateString('fr-FR')}
              </span>
            </div>
          )}
        </motion.div>

        {/* Form */}
        <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-soft p-6 space-y-6">
          {/* Profile ID Badge */}
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <span className="text-sm text-slate-500">{t('profileEdit.profileId')}: </span>
            <span className="font-mono font-bold text-[#0A2540]">{profile?.profile_id}</span>
          </div>

          {/* Profile Photo */}
          <div className="space-y-3">
            <h3 className="font-medium text-slate-900 flex items-center gap-2">
              <Camera className="w-4 h-4" />
              {t('owner.profilePhoto')}
            </h3>
            <PhotoUpload currentPhoto={photoUrl} token={token} onUploaded={setPhotoUrl} />
          </div>

          {/* Profile Banner */}
          <div className="space-y-3">
            <h3 className="font-medium text-slate-900 flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              {t('owner.profileBanner')}
            </h3>
            <BannerUpload currentBanner={banner} token={token} onUploaded={setBanner} />
          </div>

          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="font-medium text-slate-900 flex items-center gap-2">
              <User className="w-4 h-4" />
              {t('owner.basicInfo')}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('owner.firstName')}</Label>
                <Input value={formData.first_name} onChange={(e) => handleChange('first_name', e.target.value)} placeholder={t('owner.firstName')} />
              </div>
              <div className="space-y-2">
                <Label>{t('owner.lastName')}</Label>
                <Input value={formData.last_name} onChange={(e) => handleChange('last_name', e.target.value)} placeholder={t('owner.lastName')} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('owner.jobTitle')}</Label>
              <Input value={formData.job_title} onChange={(e) => handleChange('job_title', e.target.value)} placeholder={t('owner.jobTitle')} />
            </div>
            <div className="space-y-2">
              <Label>{t('owner.company')}</Label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={formData.company} onChange={(e) => handleChange('company', e.target.value)} className="pl-10" placeholder={t('owner.company')} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('owner.bio')}</Label>
              <Textarea value={formData.bio} onChange={(e) => handleChange('bio', e.target.value)} placeholder={t('owner.bioPlaceholder')} rows={3} />
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="font-medium text-slate-900 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              {t('owner.contactInfo')}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('owner.phone')}</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} className="pl-10" placeholder="+1 234 567 890" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('owner.officePhone')}</Label>
                <Input value={formData.office_phone} onChange={(e) => handleChange('office_phone', e.target.value)} placeholder="+1 234 567 890" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('owner.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} className="pl-10" placeholder="email@example.com" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('owner.website')}</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={formData.website} onChange={(e) => handleChange('website', e.target.value)} className="pl-10" placeholder="https://example.com" />
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="space-y-4">
            <h3 className="font-medium text-slate-900 flex items-center gap-2">
              <Linkedin className="w-4 h-4" />
              {t('owner.socialLinks')}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Linkedin className="w-4 h-4 text-[#0A2540]" />LinkedIn</Label>
                <Input value={formData.linkedin} onChange={(e) => handleChange('linkedin', e.target.value)} placeholder="linkedin.com/in/..." />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Instagram className="w-4 h-4 text-pink-600" />Instagram</Label>
                <Input value={formData.instagram} onChange={(e) => handleChange('instagram', e.target.value)} placeholder="@username" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><MessageCircle className="w-4 h-4 text-green-600" />WhatsApp</Label>
                <Input value={formData.whatsapp} onChange={(e) => handleChange('whatsapp', e.target.value)} placeholder="+1 234 567 890" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Facebook className="w-4 h-4 text-[#0A2540]" />Facebook</Label>
                <Input value={formData.facebook} onChange={(e) => handleChange('facebook', e.target.value)} placeholder="facebook.com/..." />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Twitter className="w-4 h-4" />X (Twitter)</Label>
                <Input value={formData.twitter} onChange={(e) => handleChange('twitter', e.target.value)} placeholder="@username" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Github className="w-4 h-4" />GitHub</Label>
                <Input value={formData.github} onChange={(e) => handleChange('github', e.target.value)} placeholder="github.com/..." />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Youtube className="w-4 h-4 text-red-600" />{t('owner.youtubeChannel')}</Label>
                <Input value={formData.youtube_channel} onChange={(e) => handleChange('youtube_channel', e.target.value)} placeholder="youtube.com/..." />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Youtube className="w-4 h-4 text-red-600" />{t('owner.youtubeVideo')}</Label>
                <Input value={formData.youtube_video} onChange={(e) => handleChange('youtube_video', e.target.value)} placeholder="youtube.com/watch?v=..." />
              </div>
            </div>
          </div>

          {/* Submit */}
          <Button type="submit" disabled={saving} className="w-full btn-primary h-12 rounded-full text-lg" data-testid="save-profile-btn">
            {saving ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t('common.saving')}</>
            ) : (
              <><Save className="w-5 h-5 mr-2" />{t('profileEdit.saveChanges')}</>
            )}
          </Button>
        </motion.form>
      </div>
    </div>
  );
}
